/// <reference types="@types/google.maps" />
import { MediaMatcher } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { UserState } from 'src/app/_shared/state/user.state';
import { WorkspaceState } from 'src/app/_shared/state/workspace.state';
import { AppState } from 'src/app/app.state';
import { MapService } from 'src/app/workspace/service/map.service';
import { MarkerService } from 'src/app/workspace/service/marker.service';
import { MapState } from 'src/app/workspace/state/map.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import constants from 'src/constant.json';
import {
  GeoJSONStoreFeatures,
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { MapFacade } from '../map.facade';

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss'],
  //changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('drawer') drawer: MatDrawer;
  companyName: string = '';
  userType: string = '';
  activeWorkspaceMode: any = {};
  clusterClickEvent;
  terraDraw: TerraDraw;
  drawnShape;
  gMapClickEvent;
  gMarkerClickEvent;
  idleEvent;
  infoWindow: google.maps.InfoWindow;
  markerList: Array<any> = [];
  mobileQuery: MediaQueryList;
  openPropertyListStatus: boolean = true;
  private mobileQueryListener: () => void;
  searchInProgress: boolean = false;
  searchSuccessMessage = null;
  showMapOnly = true;
  zoomChangedEvent;
  rendererListener;

  // Subscriptions
  // getMarkerList$: Subscription = new Subscription();
  // initMarkerRender$: Subscription = new Subscription();
  // isLoading$: Subscription = new Subscription();

  // Subscription to unsubscribe on component destroy
  onDestroyNotifier$ = new Subject();

  constructor(
    media: MediaMatcher,
    private authorizeService: AuthorizeService,
    private cdr: ChangeDetectorRef,
    private mapService: MapService,
    private mapState: MapState,
    private markerService: MarkerService,
    public mapFacade: MapFacade,
    public propertyState: PropertyState,
    public workspaceState: WorkspaceState,
    private renderer: Renderer2,
    private _zone: NgZone,
    public appState: AppState,
    private userState: UserState,
    private activatedRoute: ActivatedRoute
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 767.98px)');
    this.mobileQuery.addListener(this.mobileQueryListener);
    this.rendererListener = this.renderer.listen('window', 'click', (event: any) => {
      if (!event.target.classList.contains('map-info-box') && this.mapState.activeInfoWindowValue)
        this.mapState.activeInfoWindowValue.close();
    });
  }

  ngOnInit() {
    this.appState.workspaceView = 'map';
    // this.initMarkerGeneration();
    this.openPropertyListStatus = !this.mobileQuery.matches;
    this.propertyState.mapPropertySet$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe(() => {
      this.cdr.detectChanges();
    });

    this.propertyState.searchApiProgress$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.searchInProgress = res;
      this.cdr.detectChanges();
    });

    const hasMenu = this.appState.companyInfoValue?.customizationConfig?.companyNavBar?.[0]?.config?.menuItems;
    const headerHeight = this.appState.authTokenValue?.idToken ? (hasMenu ? 130 : 140) : hasMenu ? 200 : 145;
    document.getElementById('map')!.style.height = `calc(100vh - ${headerHeight}px)`;

    this.userType = this.userState.userDetails?.userType;
    this.companyName = this.appState && this.appState.companyInfoValue.alias;
  }

  ngAfterViewInit() {
    this.loadGoogleMap();
    this.initSubscription();
    this.initTerraDraw();
    this.showMapOnly = true;
  }

  initTerraDraw() {
    google.maps.event.addListenerOnce(this.mapState.workspaceMap, 'projection_changed', () => {
      this.terraDraw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map: this.mapState.workspaceMap,
          lib: google.maps,
          coordinatePrecision: 9,
        }),
        modes: [
          new TerraDrawSelectMode(),
          new TerraDrawPolygonMode({
            styles: { fillColor: '#e8ebf4', fillOpacity: 0.4, outlineWidth: 0.8 },
          }),
          new TerraDrawCircleMode({
            styles: { fillColor: '#e8ebf4', fillOpacity: 0.6, outlineWidth: 0.8 },
          }),
        ],
      });
      this.terraDraw.start();
      this.terraDraw.setMode('static');

      this.terraDraw.on('finish', (id) => {
        const feature = this.terraDraw.getSnapshot().find((f) => f.id === id);
        if (!feature) return;
        this.handleDrawnFeature(feature);
        this.terraDraw.setMode('static');
      });
    });
  }

  // Converts a finished Terra Draw GeoJSON feature back into a google.maps overlay,
  // preserving the shape of the rest of your code (drawnShape, updateMarkersInDrawing).
  handleDrawnFeature(feature: GeoJSONStoreFeatures) {
    const mode = feature.properties.mode as string; // 'polygon' | 'circle'
    const ring = (feature.geometry as any).coordinates[0] as number[][];
    const path = ring.map(([lng, lat]) => new google.maps.LatLng(lat, lng));

    if (mode === 'circle') {
      // Terra Draw represents circles as approximated polygons; derive center/radius from the ring.
      const bounds = new google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      const center = bounds.getCenter();
      const radius = google.maps.geometry?.spherical
        ? google.maps.geometry.spherical.computeDistanceBetween(center, path[0])
        : 0;
      this.drawnShape = new google.maps.Circle({ center, radius, map: this.mapState.workspaceMap });
      this.drawnShape.type = 'circle';
    } else {
      this.drawnShape = new google.maps.Polygon({ paths: path, map: this.mapState.workspaceMap });
      this.drawnShape.type = 'polygon';
    }

    this.markerService.updateMarkersInDrawing(this.drawnShape, this.drawnShape.type);
  }

  loadGoogleMap() {
    this.mapState.workspaceMap = this._zone.runOutsideAngular(() => {
      return new google.maps.Map(
        document.getElementById('map') as HTMLElement,
        {
          zoom: this.mapFacade.getDefaultZoom(),
          styles: this.mapFacade.getMapTheme(),
          disableDefaultUI: true,
          zoomControl: true,
          scrollwheel: true,
          clickableIcons: false,
        } as google.maps.MapOptions
      );
    });
  }

  ngOnDestroy() {
    google.maps.event.removeListener(this.idleEvent);
    google.maps.event.removeListener(this.zoomChangedEvent);
    google.maps.event.removeListener(this.gMapClickEvent);
    google.maps.event.removeListener(this.clusterClickEvent);
    google.maps.event.removeListener(this.gMarkerClickEvent);
    if (this.terraDraw) {
      this.terraDraw.stop();
    }
    this.mapFacade.clearGoogleMarkerList();
    this.rendererListener && this.rendererListener();
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  initSubscription() {
    this.idleEvent = google.maps.event.addListener(this.mapState.workspaceMap, 'idle', () => {
      this.clearDrawing('IdleState');
      this.updateBoundingBox();
      this.updateCenter();
      this.updateZoom();
    });

    // this.zoomChangedEvent = google.maps.event.addListener(this.gMap, 'zoom_changed', () => {
    //   this.clearDrawing('ZoomChanged');
    //   this.updateZoom();
    // });

    this.gMapClickEvent = google.maps.event.addListener(this.mapState.workspaceMap, 'click', () => {
      this.mapService.closeActiveInfoWindow();
    });

    this.mapFacade
      .getMapZoom$()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        //console.log('$#%@ map-view-component-1'); // do not remove
        if (this.mapState.workspaceMap.getZoom() != this.mapFacade.getMapZoom()) {
          this.mapState.workspaceMap.setZoom(res);
        }
      });

    this.mapFacade
      .getCenter$()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        //console.log('$#%@ map-view-component-2'); // do not remove
        this.mapState.workspaceMap.setCenter(res);
      });

    this.mapFacade
      .getSearchSuccessMessage$()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.searchSuccessMessage = res;
      });

    this.workspaceState.activeWorkspaceMode$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((mode) => {
      this.activeWorkspaceMode = mode;
      this.activeWorkspaceMode = !this.activeWorkspaceMode.isDefault ? false : true;
    });
  }

  updateBoundingBox() {
    if (this.mapState.workspaceMap && this.mapState.workspaceMap.getBounds()) {
      const bounds = this.mapState.workspaceMap.getBounds();
      this.mapFacade.setBoundingBox({
        NELatitude: bounds.getNorthEast().lat(),
        NELongitude: bounds.getNorthEast().lng(),
        SWLatitude: bounds.getSouthWest().lat(),
        SWLongitude: bounds.getSouthWest().lng(),
      });
    }
  }

  updateCenter() {
    if (this.mapState.workspaceMap && this.mapState.workspaceMap.getCenter()) {
      const center = this.mapState.workspaceMap.getCenter().toJSON();
      this.mapFacade.setCenter(center);
    }
  }

  updateZoom() {
    if (this.mapState.workspaceMap) {
      const currentZoom = this.mapState.workspaceMap.getZoom();
      if (currentZoom < constants.PROPERTY_MAP.minZoomLevel) {
        return this.mapState.workspaceMap.setZoom(constants.PROPERTY_MAP.minZoomLevel);
      }
      if (currentZoom > constants.PROPERTY_MAP.maxZoomLevel) {
        return this.mapState.workspaceMap.setZoom(constants.PROPERTY_MAP.maxZoomLevel);
      }
      this.mapService.setZoom(currentZoom);
    }
  }

  initDrawing(type: string) {
    if (this.authorizeService.canAct('mapControl') && this.terraDraw) {
      this.clearDrawing('InitDrawing');
      this.terraDraw.setMode(type); // 'polygon' or 'circle'
    }
  }

  clearDrawing(from) {
    if (this.terraDraw) {
      this.terraDraw.clear();
      this.terraDraw.setMode('static');
    }
    if (this.drawnShape && this.drawnShape !== null) {
      this.drawnShape.setMap(null);
      this.drawnShape = null;
    }
  }

  closeFollowDropDown() {
    // To close the perticular modal
  }
  closeRecentSearchDropDown() {
    // To close the perticular modal
  }
  closeViewSearchDropDown() {
    // To close the perticular modal
  }
  changeMapControls() {
    this.showMapOnly = !this.showMapOnly;
    this.mapFacade.setShowMapControls(this.showMapOnly);
  }

  showOnMap(PMXPropertyId) {
    const selectedMarker = this.markerList.find((gMarker) => {
      return gMarker.PMXPropertyId === PMXPropertyId;
    });
    if (selectedMarker) {
      this.gMarkerClickEvent = google.maps.event.trigger(selectedMarker, 'click');
    }
  }

  togglePropertyList() {
    this.openPropertyListStatus = !this.openPropertyListStatus;
  }

  updateArrowStatus(event) {
    this.openPropertyListStatus = event;
  }
}